import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const ProductsFilters = ({ filters, categories, onFilterChange, onClearFilters, activeFiltersCount }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        onFilterChange({ ...filters, search: searchValue });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Update local search when filters change externally
  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  const handleQuickFilter = (filterType) => {
    switch (filterType) {
      case 'active':
        // Toggle active - mutually exclusive with inactive and lowStock
        if (filters.status === 'active') {
          // Clicking again clears the filter
          onFilterChange({ ...filters, status: '', lowStock: false });
        } else {
          // Set active and clear lowStock (they're mutually exclusive quick filters)
          onFilterChange({ ...filters, status: 'active', lowStock: false });
        }
        break;
      case 'inactive':
        // Toggle inactive - mutually exclusive with active and lowStock
        if (filters.status === 'inactive') {
          onFilterChange({ ...filters, status: '', lowStock: false });
        } else {
          onFilterChange({ ...filters, status: 'inactive', lowStock: false });
        }
        break;
      case 'lowStock':
        // Toggle lowStock - mutually exclusive with status filters
        if (filters.lowStock) {
          onFilterChange({ ...filters, lowStock: false, status: '' });
        } else {
          onFilterChange({ ...filters, lowStock: true, status: '' });
        }
        break;
      case 'featured':
        // Add featured filter when available
        break;
      default:
        break;
    }
  };

  return (
    <div className="bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 p-6 rounded-xl shadow-md border border-gray-200 mb-6">
      {/* Main Search */}
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            🔍 חיפוש מוצרים
          </label>
          <div className="relative">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="חפש לפי שם, SKU או תיאור..."
              className="w-full pr-10 pl-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2" />
            {searchValue && (
              <button
                onClick={() => setSearchValue('')}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 transition-all shadow-sm hover:shadow-md ${
            showAdvanced
              ? 'bg-gradient-to-r from-blue-500 to-indigo-600 border-blue-600 text-white'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FunnelIcon className="h-5 w-5" />
          <span className="font-semibold">מסננים</span>
          {activeFiltersCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-white text-blue-600 rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </button>

        {activeFiltersCount > 0 && (
          <button
            onClick={onClearFilters}
            className="flex items-center gap-2 px-4 py-3 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all font-semibold border-2 border-transparent hover:border-red-200"
          >
            <XMarkIcon className="h-5 w-5" />
            נקה הכל
          </button>
        )}
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-3 mt-5">
        <button
          onClick={() => handleQuickFilter('active')}
          className={`px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-sm hover:shadow-md transform hover:scale-105 ${
            filters.status === 'active'
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white border-2 border-green-600'
              : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300'
          }`}
        >
          ✅ פעילים
        </button>
        <button
          onClick={() => handleQuickFilter('inactive')}
          className={`px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-sm hover:shadow-md transform hover:scale-105 ${
            filters.status === 'inactive'
              ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white border-2 border-red-600'
              : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300'
          }`}
        >
          ❌ לא פעילים
        </button>
        <button
          onClick={() => handleQuickFilter('lowStock')}
          className={`px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-sm hover:shadow-md transform hover:scale-105 ${
            filters.lowStock
              ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white border-2 border-orange-600'
              : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-300'
          }`}
        >
          ⚠️ מלאי נמוך
        </button>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="mt-6 pt-6 border-t-2 border-dashed border-gray-300 animate-fadeIn">
          <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
            🎯 פילטרים מתקדמים
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                📁 קטגוריה
              </label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all font-medium"
                value={filters.category}
                onChange={(e) => onFilterChange({ ...filters, category: e.target.value })}
              >
                <option value="">כל הקטגוריות</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                ⚡ סטטוס
              </label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all font-medium"
                value={filters.status}
                onChange={(e) => onFilterChange({ ...filters, status: e.target.value })}
              >
                <option value="">כל הסטטוסים</option>
                <option value="active">✅ פעיל</option>
                <option value="inactive">❌ לא פעיל</option>
                <option value="draft">📝 טיוטה</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                💰 טווח מחירים
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="מ-"
                  className="w-1/2 px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all font-medium"
                  value={filters.minPrice}
                  onChange={(e) => onFilterChange({ ...filters, minPrice: e.target.value })}
                />
                <input
                  type="number"
                  placeholder="עד-"
                  className="w-1/2 px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all font-medium"
                  value={filters.maxPrice}
                  onChange={(e) => onFilterChange({ ...filters, maxPrice: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                🔄 מיון
              </label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all font-medium"
                value={`${filters.sortBy}:${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split(':');
                  onFilterChange({ ...filters, sortBy, sortOrder });
                }}
              >
                <option value="created_at:desc">🆕 חדש ביותר</option>
                <option value="created_at:asc">🕰️ ישן ביותר</option>
                <option value="name:asc">📝 שם (א-ת)</option>
                <option value="name:desc">📝 שם (ת-א)</option>
                <option value="price_b2c:asc">💵 מחיר (נמוך-גבוה)</option>
                <option value="price_b2c:desc">💰 מחיר (גבוה-נמוך)</option>
                <option value="stock_quantity:asc">📉 מלאי (נמוך-גבוה)</option>
                <option value="stock_quantity:desc">📈 מלאי (גבוה-נמוך)</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsFilters;

